/**
 * NewBillDialog — capture a vendor bill and post it through the GL.
 * Lines may target WIP (jobId) or any expense account; one or more.
 */
import { useEffect, useMemo, useState } from "react";
import { Plus, Sparkles, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { useAP } from "@/data/accounting/ap/APContext";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";
import { useVendorPO } from "@/data/VendorPOContext";
import { useAppData } from "@/data/AppDataContext";
import { suggestVendorAccount } from "@/data/accounting/vendorMemory";

interface LineDraft {
  glAccountId: string;
  amount: string;
  description: string;
  jobId?: string;
  tradeType?: string;
  costType?: "material" | "labor" | "other";
  /** Optional contractor-labor capture; when both set, amount = hours*rate. */
  hours?: string;
  rate?: string;
  /** Track whether the user has manually overridden the account so we don't
   *  clobber their choice when the job changes. */
  accountTouched?: boolean;
}

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const EXPENSE_PARENTS = new Set(["expense"]);

export interface NewBillDialogInitial {
  /** Vendor display name — resolved via case-insensitive substring match. */
  vendorName?: string;
  invoiceNumber?: string;
  /** Total bill amount; lands on the first line if provided. */
  amount?: number;
  /** Bill date (yyyy-mm-dd). Due date is set 30 days later when only this is provided. */
  billDate?: string;
  description?: string;
}

export function NewBillDialog({ open, onOpenChange, initial, onPosted }: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  initial?: NewBillDialogInitial;
  onPosted?: (bill: ReturnType<ReturnType<typeof useAP>["createBill"]>) => void;
}) {
  const { createBill } = useAP();
  const { accounts, journalEntries } = useGeneralLedger();
  const { vendors } = useVendorPO();
  const { jobs } = useAppData();

  const [vendorId, setVendorId] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [billDate, setBillDate] = useState(today());
  const [dueDate, setDueDate] = useState(plusDays(30));
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<LineDraft[]>([
    { glAccountId: "1200", amount: "", description: "", costType: "material" },
  ]);

  // Apply prefill when the dialog opens with initial data (e.g. from Document Inbox).
  useEffect(() => {
    if (!open || !initial) return;
    if (initial.vendorName) {
      const match = vendors.find(v =>
        v.vendorName.toLowerCase().includes(initial.vendorName!.toLowerCase()) ||
        initial.vendorName!.toLowerCase().includes(v.vendorName.toLowerCase()));
      if (match) setVendorId(match.vendorId);
    }
    if (initial.invoiceNumber) setInvoiceNumber(initial.invoiceNumber);
    if (initial.billDate) {
      setBillDate(initial.billDate);
      const d = new Date(initial.billDate);
      if (!isNaN(d.getTime())) {
        d.setDate(d.getDate() + 30);
        setDueDate(d.toISOString().slice(0, 10));
      }
    }
    if (initial.amount != null || initial.description) {
      setLines(ls => {
        const next = [...ls];
        next[0] = {
          ...next[0],
          amount: initial.amount != null ? String(initial.amount) : next[0].amount,
          description: initial.description ?? next[0].description,
        };
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const activeVendors = vendors.filter(v => v.active !== false);
  const debitAccounts = accounts.filter(a =>
    a.active && (a.type === "expense" || a.subtype === "wip"));

  // Vendor categorization memory (D-3): derive a suggested account whenever
  // the selected vendor changes. Cold-start (no history) returns null and we
  // leave the existing default in place — no autofill noise on first use.
  const suggestion = useMemo(
    () => (vendorId ? suggestVendorAccount(vendorId, journalEntries) : null),
    [vendorId, journalEntries],
  );
  const suggestedAccountName = useMemo(() => {
    if (!suggestion) return null;
    const acc = accounts.find(a => a.id === suggestion.accountId);
    return acc ? `${acc.number} · ${acc.name}` : suggestion.accountId;
  }, [suggestion, accounts]);

  // When a suggestion lands and the first line is still on the default
  // (1200 WIP, unfilled amount), pre-fill it with the learned account so the
  // operator only has to confirm. They can always change it.
  useEffect(() => {
    if (!suggestion) return;
    setLines(ls => {
      const first = ls[0];
      if (!first || first.amount || first.glAccountId !== "1200") return ls;
      const next = [...ls];
      next[0] = { ...first, glAccountId: suggestion.accountId };
      return next;
    });
  }, [suggestion]);

  const reset = () => {
    setVendorId(""); setInvoiceNumber(""); setBillDate(today());
    setDueDate(plusDays(30)); setNotes("");
    setLines([{ glAccountId: "1200", amount: "", description: "", costType: "material" }]);
  };

  const submit = () => {
    if (!vendorId) return toast({ title: "Pick a vendor", variant: "destructive" });
    if (!invoiceNumber.trim()) return toast({ title: "Invoice # required", variant: "destructive" });
    const vendor = vendors.find(v => v.vendorId === vendorId);
    if (!vendor) return;

    const parsed = lines.map(l => {
      const hoursNum = l.hours ? parseFloat(l.hours) : undefined;
      const rateNum = l.rate ? parseFloat(l.rate) : undefined;
      const computed = hoursNum && rateNum ? Math.round(hoursNum * rateNum * 100) / 100 : undefined;
      const amount = computed ?? parseFloat(l.amount);
      return {
        glAccountId: l.glAccountId,
        amount,
        description: l.description,
        jobId: l.jobId || undefined,
        tradeType: l.tradeType || undefined,
        costType: l.costType,
        hours: Number.isFinite(hoursNum) ? hoursNum : undefined,
        rate: Number.isFinite(rateNum) ? rateNum : undefined,
      };
    });
    if (parsed.some(l => !l.glAccountId || Number.isNaN(l.amount) || l.amount <= 0)) {
      return toast({ title: "Every line needs an account + positive amount", variant: "destructive" });
    }
    // WIP lines need a job
    if (parsed.some(l => l.glAccountId === "1200" && !l.jobId)) {
      return toast({ title: "WIP lines require a job (WO#)", variant: "destructive" });
    }
    try {
      const bill = createBill({
        vendorId, vendorName: vendor.vendorName,
        invoiceNumber, billDate, dueDate, lines: parsed, notes,
      });
      toast({ title: "Bill posted", description: `${vendor.vendorName} · ${invoiceNumber}` });
      reset();
      onOpenChange(false);
      onPosted?.(bill);
    } catch (e) {
      toast({ title: "Couldn't post bill", description: (e as Error).message, variant: "destructive" });
    }
  };

  const total = lines.reduce((s, l) => {
    const h = parseFloat(l.hours ?? ""); const r = parseFloat(l.rate ?? "");
    if (Number.isFinite(h) && Number.isFinite(r)) return s + h * r;
    return s + (parseFloat(l.amount) || 0);
  }, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>New Vendor Bill</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Vendor</Label>
              <Select value={vendorId} onValueChange={setVendorId}>
                <SelectTrigger><SelectValue placeholder="Select vendor" /></SelectTrigger>
                <SelectContent>
                  {activeVendors.map(v => (
                    <SelectItem key={v.vendorId} value={v.vendorId}>{v.vendorName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {suggestion && suggestedAccountName && (
                <div className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
                  <Sparkles className="h-3 w-3 text-primary" />
                  <span>
                    Suggested:{" "}
                    <span className="font-medium text-foreground">{suggestedAccountName}</span>{" "}
                    <span className="text-muted-foreground">
                      ({Math.round(suggestion.confidence * 100)}% · {suggestion.sampleSize} prior)
                    </span>
                  </span>
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Invoice #</Label>
              <Input value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Bill date</Label>
              <Input type="date" value={billDate} onChange={e => setBillDate(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Due date</Label>
              <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs">Lines</Label>
              <Button size="sm" variant="ghost" className="h-6 px-2"
                onClick={() => setLines(ls => [...ls, { glAccountId: "1200", amount: "", description: "", costType: "material" }])}>
                <Plus className="h-3 w-3 mr-1" /> Add line
              </Button>
            </div>
            <div className="space-y-2">
              {lines.map((l, i) => {
                const hoursNum = l.hours ? parseFloat(l.hours) : NaN;
                const rateNum = l.rate ? parseFloat(l.rate) : NaN;
                const computed = Number.isFinite(hoursNum) && Number.isFinite(rateNum)
                  ? Math.round(hoursNum * rateNum * 100) / 100 : null;
                const amountLocked = computed !== null;
                const isWip = l.glAccountId === "1200";
                const updateLine = (patch: Partial<LineDraft>) =>
                  setLines(ls => ls.map((x, idx) => idx === i ? { ...x, ...patch } : x));
                return (
                <div key={i} className="rounded-sm border border-border p-2 space-y-2">
                  {/* Row 1: Project → auto-suggests WIP → Account (overridable) */}
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-4">
                      <Label className="text-[10px] uppercase text-muted-foreground">Project (WO#)</Label>
                      <Select value={l.jobId ?? "__none"}
                        onValueChange={v => {
                          const jobId = v === "__none" ? undefined : v;
                          // Steve's rule: picking a project routes to WIP automatically
                          // — unless the operator has already chosen a different account.
                          const autoAccount = jobId && !l.accountTouched ? "1200" : l.glAccountId;
                          updateLine({ jobId, glAccountId: autoAccount });
                        }}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="— None —" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none" className="text-xs">— None —</SelectItem>
                          {jobs.slice(0, 50).map(j => (
                            <SelectItem key={j.id} value={j.id} className="text-xs">
                              {j.id} · {j.customerName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-5">
                      <Label className="text-[10px] uppercase text-muted-foreground">
                        Account {isWip && l.jobId && <span className="text-primary">· auto-routed to WIP</span>}
                      </Label>
                      <Select value={l.glAccountId}
                        onValueChange={v => updateLine({ glAccountId: v, accountTouched: true })}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {debitAccounts.map(a => (
                            <SelectItem key={a.id} value={a.id} className="text-xs">
                              {a.number} · {a.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px] uppercase text-muted-foreground">Cost type</Label>
                      <Select value={l.costType ?? "material"}
                        onValueChange={(v: "material" | "labor" | "other") => updateLine({ costType: v })}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="material" className="text-xs">Material</SelectItem>
                          <SelectItem value="labor" className="text-xs">Labor (1099)</SelectItem>
                          <SelectItem value="other" className="text-xs">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Row 2: Description + optional Hours × Rate + Amount */}
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-5">
                      <Label className="text-[10px] uppercase text-muted-foreground">Description</Label>
                      <Input value={l.description} className="h-8 text-xs"
                        onChange={e => updateLine({ description: e.target.value })} />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[10px] uppercase text-muted-foreground">Hours</Label>
                      <Input value={l.hours ?? ""} inputMode="decimal" placeholder="opt."
                        className="h-8 text-xs tabular-nums text-right"
                        onChange={e => updateLine({ hours: e.target.value })} />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[10px] uppercase text-muted-foreground">Rate</Label>
                      <Input value={l.rate ?? ""} inputMode="decimal" placeholder="opt."
                        className="h-8 text-xs tabular-nums text-right"
                        onChange={e => updateLine({ rate: e.target.value })} />
                    </div>
                    <div className="col-span-3 flex items-end gap-1">
                      <div className="flex-1">
                        <Label className="text-[10px] uppercase text-muted-foreground">
                          Amount {amountLocked && <span className="text-primary">· hrs×rate</span>}
                        </Label>
                        <Input
                          value={amountLocked ? computed!.toFixed(2) : l.amount}
                          inputMode="decimal"
                          disabled={amountLocked}
                          className="h-8 text-xs tabular-nums text-right"
                          onChange={e => updateLine({ amount: e.target.value })} />
                      </div>
                      {lines.length > 1 && (
                        <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0"
                          onClick={() => setLines(ls => ls.filter((_, idx) => idx !== i))}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
            <div className="flex justify-end pt-2 text-sm">
              <span className="text-muted-foreground mr-2">Total</span>
              <span className="font-semibold tabular-nums">
                ${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>

          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} className="h-16 text-xs" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit}>Post Bill</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
